import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FaEnvelope, FaXTwitter } from "react-icons/fa6";
import API from "../../services/api";
import LoginStepOne from "../Auth/LoginStepOne";
import { isAuthenticated } from "../../utils/auth";
import CandidateHeader from "./CandidateHeader";
import "./candidatejobdetails.css";

const formatJobDescription = (description = "") => {
  const normalizedDescription = description.replace(/\r\n/g, "\n").trim();

  if (!normalizedDescription) {
    return [];
  }

  return normalizedDescription
    .split(/\n{2,}/)
    .map((section) => section.trim())
    .filter(Boolean);
};

const toTitleCase = (value = "") =>
  value.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatJobLocation = (location = "") => {
  const city = toTitleCase(location.trim());

  if (!city) {
    return "";
  }

  if (city.includes(",")) {
    return city
      .split(",")
      .map((part) => toTitleCase(part.trim()))
      .filter(Boolean)
      .join(", ");
  }

  const cityStateMap = {
    Hyderabad: "Telangana",
    Kochi: "Kerala",
    Bangalore: "Karnataka",
    Bengaluru: "Karnataka",
    Chennai: "Tamil Nadu",
    Mumbai: "Maharashtra",
  };

  const state = cityStateMap[city] || "Telangana";

  return `${city}, ${state}, India`;
};

const boldDescriptionLabels = [
  "Key Responsibilities",
  "Required Skills",
  "Eligibility",
  "Perks & Benefits",
];

const renderDescriptionSection = (section) => {
  const labelPattern = new RegExp(
    `(${boldDescriptionLabels.map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "g",
  );

  return section.split(labelPattern).map((part, index) => {
    if (boldDescriptionLabels.includes(part)) {
      return (
        <strong key={`${part}-${index}`} className="job-description-label">
          {part}
        </strong>
      );
    }

    return part;
  });
};

const CandidateJobDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { jobCode } = useParams();

  const [job, setJob] = useState(location.state?.job || null);
  const [loading, setLoading] = useState(!location.state?.job);
  const [showLoginStepOne, setShowLoginStepOne] = useState(false);

  useEffect(() => {
    if (!job) {
      const fetchJobs = async () => {
        try {
          const res = await API.get("/jobs");
          const jobs = res.data?.data || [];
          const selectedJob = jobs.find((j) => j.id === jobCode);

          setJob(selectedJob);
        } catch (error) {
          console.error("Error fetching job:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchJobs();
    }
  }, [jobCode, job]);

  const applyState = {
    from: `/candidate/candidatecareer/${job?.id || jobCode}`,
  };

  const handleApplyNow = () => {
    if (isAuthenticated()) {
      navigate(`/apply/${job.id}`, { state: applyState });
      return;
    }

    setShowLoginStepOne(true);
  };

  const handleLoginSuccess = () => {
    navigate(`/apply/${job.id}`, { state: applyState });
  };

  const getCurrentJobUrl = () => {
    if (typeof window === "undefined") {
      return "";
    }

    return window.location.href;
  };

  const openShareWindow = (shareUrl) => {
    if (typeof window === "undefined") {
      return;
    }

    window.open(shareUrl, "_blank", "noopener,noreferrer,width=720,height=640");
  };

  const handleEmailShare = () => {
  const publicUrl = `https://blackroth.in/careers/${job.id}`;
  const subject = encodeURIComponent(job.title);
  const body = encodeURIComponent(`Check this job opening: ${publicUrl}`);
  const outlookWebUrl = `https://outlook.office.com/mail/deeplink/compose?subject=${subject}&body=${body}`;

  window.open(outlookWebUrl, "_blank");
};


  const handleTwitterShare = () => {
  const publicUrl = `https://blackroth.in/careers/${job.id}`;
  const text = encodeURIComponent(job.title);
  const url = encodeURIComponent(publicUrl);

  openShareWindow(`https://twitter.com/intent/tweet?text=${text}&url=${url}`);
};

  const handleLinkedInShare = () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    console.log(baseUrl)

    const publicUrl = `${baseUrl}/api/public/careers/linkedin/${job.id}`;
    
    console.log(publicUrl)
    //const publicUrl = API.get(`api/public/careers/linkedin/${job.id}`); // ✅ use public page
    const encodedUrl = encodeURIComponent(publicUrl);
    console.log(encodedUrl)

    openShareWindow(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    );
  };

  const descriptionSections = formatJobDescription(job?.description);
  const jobLocation = formatJobLocation(job?.location);
  const jobProfileType = "Professional";
  const jobUrl = getCurrentJobUrl();
  const shareImageUrl = "https://blackroth-images.s3.ap-south-1.amazonaws.com/BlackRothmail.png";
  const pageTitle = `${job?.title || "Career Opportunity"} | BlackRoth`;
  const pageDescription = job?.description
    ? `${job.description.replace(/\s+/g, " ").trim().slice(0, 150)}...`
    : `Apply for ${job?.title || "this role"} at BlackRoth.`;

  if (loading) {
    return (
      <>
        <CandidateHeader withSpacer />
        <div className="jobdetails-wrapper">
          <div className="jobdetails-container">
            <div className="jobdetails-inner jobdetails-state">
              <h2>Loading job details...</h2>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!job) {
    return (
      <>
        <CandidateHeader withSpacer />
        <div className="jobdetails-wrapper">
          <div className="jobdetails-container">
            <div className="jobdetails-inner jobdetails-state">
              <h2>Job not found</h2>

              <div className="jobdetails-links">
                <Link to="/" className="back-link">
                  &larr; Back to Home
                </Link>

                <Link to="/candidate/candidatecareer" className="back-link">
                  &larr; Back to Careers
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (showLoginStepOne) {
    return <LoginStepOne onSuccess={handleLoginSuccess} />;
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={shareImageUrl} />
        <meta property="og:url" content={jobUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BlackRoth" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={shareImageUrl} />
      </Helmet>

      <CandidateHeader withSpacer />
      <div className="jobdetails-wrapper">
        <div className="jobdetails-container">
          <section className="jobdetails-hero">
            <div className="jobdetails-inner">
              <div className="jobdetails-nav-links">
                <Link
                  to="/candidate/candidatedashboard?tab=overview"
                  className="back-link"
                >
                  &lt; Back to overview
                </Link>

                <Link to="/candidate/candidatecareer" className="back-link">
                  &lt; Back to careers
                </Link>
              </div>

              <div className="jobdetails-heading">
                <h1 className="job-title">{job.title}</h1>

                <div className="job-meta-row" aria-label="Job information">
                  {jobLocation && <span>{jobLocation}</span>}

                  <span>{jobProfileType}</span>

                  <div className="job-share-actions" aria-label="Share job">
                    <button type="button" className="job-share-btn mail-share" aria-label="Share by email" onClick={handleEmailShare}>
                      <FaEnvelope aria-hidden="true" />
                    </button>
                    <button type="button" className="job-share-btn x-share" aria-label="Share on X" onClick={handleTwitterShare}>
                      <FaXTwitter aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="job-share-btn linkedin-share"
                      aria-label="Share on LinkedIn"
                      onClick={handleLinkedInShare}
                    >
                      in
                    </button>
                  </div>
                </div>
              </div>

              <button className="apply-btn" onClick={handleApplyNow}>
                Apply now
              </button>
            </div>
          </section>

          <section className="jobdetails-body">
            <div className="jobdetails-inner">
              <div className="jobdetails-grid">
                <div className="job-description-block">
                  <h2 className="job-desc-title">Introduction</h2>

                  <div className="job-description-content">
                    {descriptionSections.length > 0 ? (
                      descriptionSections.map((section, index) => (
                        <p
                          key={`${job.id || jobCode}-description-${index}`}
                          className="job-description"
                        >
                          {renderDescriptionSection(section)}
                        </p>
                      ))
                    ) : (
                      <p className="job-description">
                        No description available.
                      </p>
                    )}
                  </div>
                </div>

                <aside className="job-summary" aria-label="Job summary">
                  <div className="job-summary-item">
                    <h2>Job Title</h2>
                    <p>{job.title}</p>
                  </div>

                  <div className="job-summary-item">
                    <h2>Job ID</h2>
                    <p>{job.job_code}</p>
                  </div>
                </aside>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default CandidateJobDetailsPage;
